import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Buildings, Plus, PencilSimple, Copy, Eye, Power, SignOut, User,
} from "@phosphor-icons/react";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * Phase 28.3 — Super Admin SaaS Control Panel (`/super`).
 *
 * Fully client-side management surface for the multi-tenant platform.
 * No more curl, no more manual DB work to onboard a pilot.
 *
 * Scope (Step 1 — usability first, not UX redesign):
 *   • List tenants (name, slug, status, email, phone).
 *   • Create a tenant + its first company_admin atomically.
 *   • Edit every tenant-editable field (contact, branding, content, legal).
 *   • Toggle active/suspended.
 *   • Copy public links (landing / booking / driver).
 *   • Open the public landing in a new tab.
 *
 * Access control is enforced by the existing ProtectedRoute (super_admin
 * only); this page does an extra soft-check on mount to bounce any
 * non-super user who sneaks in through a stale URL.
 */

/** Slugify any French/English string for URL use. */
function slugify(s) {
  return (s || "")
    .toString()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")                       // non-alphanum → hyphen
    .replace(/^-+|-+$/g, "")                           // trim leading/trailing
    .slice(0, 32);
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Lien copié");
  } catch {
    toast.error("Impossible de copier");
  }
}

function publicOrigin() {
  // Use the current hostname for the copy-link feature. Once a dedicated
  // custom domain is set per tenant, swap this for that. For now the
  // preview/production frontend host is the canonical public URL.
  return window.location.origin;
}

function companyLinks(slug) {
  const base = publicOrigin();
  return {
    landing: `${base}/${slug}`,
    booking: `${base}/${slug}/booking`,
    driver:  `${base}/${slug}/driver`,
  };
}

// Phase 28.4 — Onboarding kit message builder. Plain-text, ready to paste
// into an email or WhatsApp. Credentials section is dropped entirely if
// the tenant was created without an inline admin.
function buildOnboardingMessage({ companyName, links, loginUrl, adminEmail, tempPwd }) {
  const lines = [
    `Bienvenue sur S.DRIVE, ${companyName} !`,
    "",
    "Vos liens publics :",
    `• Page d'accueil : ${links.landing}`,
    `• Réservation client : ${links.booking}`,
    `• Espace chauffeur : ${links.driver}`,
  ];
  if (adminEmail && tempPwd) {
    lines.push(
      "",
      "Accès administrateur :",
      `• Connexion : ${loginUrl}`,
      `• Email : ${adminEmail}`,
      `• Mot de passe temporaire : ${tempPwd}`,
      "",
      "Merci de changer le mot de passe à votre première connexion.",
    );
  }
  lines.push("", "— L'équipe S.DRIVE");
  return lines.join("\n");
}

// ────────────────────────────────────────────────────────────────────────
// Reusable row pieces (used by both the desktop table and the mobile
// card list — Phase 28.5 mobile readability fix).

function CompanyAvatar({ c }) {
  const logo = c.branding?.logo_url;
  const bg   = c.branding?.primary_color   || "#F59E0B";
  const fg   = c.branding?.secondary_color || "#0A0A0A";
  if (logo) {
    return (
      <span className="h-9 w-9 rounded shrink-0 overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <img src={logo} alt="" className="max-h-8 max-w-8 object-contain" />
      </span>
    );
  }
  return (
    <span className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold"
          style={{ background: bg, color: fg }}>
      {(c.company_name || "?").trim()[0].toUpperCase()}
    </span>
  );
}

function CopyLinkChips({ slug, links }) {
  const items = [
    ["Landing", links.landing, `copy-landing-${slug}`],
    ["Réservation", links.booking, `copy-booking-${slug}`],
    ["Chauffeur", links.driver, `copy-driver-${slug}`],
  ];
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {items.map(([lbl, url, tid]) => (
        <button key={lbl} type="button" data-testid={tid} onClick={() => copyToClipboard(url)}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-zinc-700 bg-zinc-900/60 hover:bg-zinc-800 hover:border-zinc-600 text-zinc-300">
          <Copy size={11} /> {lbl}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ slug, active }) {
  return (
    <span data-testid={`status-${slug}`} className={
      active
        ? "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
        : "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"
    }>
      <span className={active ? "w-1.5 h-1.5 rounded-full bg-emerald-400" : "w-1.5 h-1.5 rounded-full bg-rose-400"} />
      {active ? "Actif" : "Suspendu"}
    </span>
  );
}

function RowActions({ c, active, links, onKit, onEdit, onToggle }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button data-testid={`kit-${c.slug}`} variant="ghost" size="icon"
              title="Copier kit d'onboarding (liens)" onClick={onKit}
              className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800">
        <Copy size={14} />
      </Button>
      <Button data-testid={`open-landing-${c.slug}`} variant="ghost" size="icon" title="Ouvrir la page publique"
              onClick={() => window.open(links.landing, "_blank", "noopener")}
              className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800">
        <Eye size={14} />
      </Button>
      <Button data-testid={`edit-${c.slug}`} variant="ghost" size="icon" title="Modifier"
              onClick={onEdit}
              className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800">
        <PencilSimple size={14} />
      </Button>
      <Button data-testid={`toggle-${c.slug}`} variant="ghost" size="icon"
              title={active ? "Suspendre" : "Réactiver"} onClick={onToggle}
              className={active
                ? "h-8 w-8 text-zinc-400 hover:text-rose-300 hover:bg-rose-500/10"
                : "h-8 w-8 text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10"}>
        <Power size={14} />
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Create modal

const CREATE_INIT = {
  company_name: "", slug: "", email: "", phone: "", whatsapp_number: "",
  admin_name: "", admin_email: "", admin_password: "",
  primary_color: "#F59E0B",
};

function CreateCompanyDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(CREATE_INIT);
  const [slugDirty, setSlugDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 28.4 — after a successful creation, switch the modal to a
  // "delivery kit" view showing public links + admin credentials with a
  // single Copy button. The plaintext password is only available in this
  // state (we never store it in the browser beyond that).
  const [kit, setKit] = useState(null);

  const setF = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  // Auto-fill slug from company_name until the user edits it manually.
  useEffect(() => {
    if (!open) { setForm(CREATE_INIT); setSlugDirty(false); setKit(null); return; }
  }, [open]);
  useEffect(() => {
    if (slugDirty) return;
    setForm((p) => ({ ...p, slug: slugify(p.company_name) }));
  }, [form.company_name, slugDirty]);

  const slugValid = /^[a-z0-9][a-z0-9-]{1,31}$/.test(form.slug);
  const required = form.company_name.trim() && form.email.trim() && form.phone.trim() && slugValid;
  const adminFilled = form.admin_email.trim() && form.admin_password.trim();
  const adminValid = !form.admin_email.trim() || (form.admin_email.includes("@") && form.admin_password.length >= 4);

  const submit = async () => {
    if (!required) { toast.error("Remplissez nom, email, téléphone et slug valide"); return; }
    if (form.admin_email.trim() && !adminValid) { toast.error("Email admin invalide ou mot de passe < 4 caractères"); return; }
    setSaving(true);
    try {
      const payload = {
        company_id:      form.slug.trim(),  // for now tenant_id == slug (Phase 27 convention)
        company_name:    form.company_name.trim(),
        slug:            form.slug.trim(),
        email:           form.email.trim(),
        phone:           form.phone.trim(),
        whatsapp_number: form.whatsapp_number.trim(),
        branding:        { primary_color: form.primary_color },
      };
      if (adminFilled) {
        payload.admin_email    = form.admin_email.trim();
        payload.admin_password = form.admin_password;
        if (form.admin_name.trim()) payload.admin_name = form.admin_name.trim();
      }
      const { data } = await api.post("/super/companies", payload);
      toast.success("Société créée");
      // Build the onboarding kit from the form + response. The admin
      // password is kept only in local state for the duration of this
      // modal view — never persisted, never refetchable.
      const links = companyLinks(data.slug);
      const adminEmail = adminFilled ? form.admin_email.trim() : null;
      const tempPwd    = adminFilled ? form.admin_password : null;
      setKit({
        company: data,
        links,
        adminEmail,
        tempPwd,
        message: buildOnboardingMessage({
          companyName: data.company_name,
          links,
          loginUrl: `${publicOrigin()}/login`,
          adminEmail,
          tempPwd,
        }),
      });
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création échouée");
    } finally {
      setSaving(false);
    }
  };

  // Kit delivery view — shown once the POST /super/companies succeeded.
  if (kit) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="bg-[#0f0f0f] border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="kit-title">Société créée — kit d'onboarding</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Copiez le message ci-dessous et envoyez-le à <span className="text-zinc-200 font-medium">{kit.company.company_name}</span>.
              {kit.tempPwd && <> Le mot de passe n'est visible qu'ici — notez-le.</>}
            </p>
            <Textarea data-testid="kit-message" value={kit.message} readOnly rows={14}
                      className="bg-[#0a0a0a] border-zinc-800 text-zinc-200 text-xs font-mono leading-5" />
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="kit-copy" onClick={() => copyToClipboard(kit.message)}
                      className="bg-amber-500 hover:bg-amber-400 text-black">
                <Copy size={14} className="mr-1.5" /> Copier le kit d'onboarding
              </Button>
              <Button variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                      onClick={() => window.open(kit.links.landing, "_blank", "noopener")}>
                <Eye size={14} className="mr-1.5" /> Voir la page publique
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="kit-close" onClick={onClose}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Creation form

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="create-company-title">Créer une société</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nom de la société *</Label>
            <Input data-testid="create-company-name" value={form.company_name} onChange={setF("company_name")}
                   placeholder="Elite VTC" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>Slug (URL publique) *</Label>
            <Input data-testid="create-company-slug" value={form.slug}
                   onChange={(e) => { setSlugDirty(true); setF("slug")(e); }}
                   placeholder="elite-vtc" className="font-mono" />
            <p className="text-xs text-zinc-500">
              URL : <span className="text-zinc-300">{publicOrigin()}/<span className="text-amber-400">{form.slug || "votre-slug"}</span></span>
              {!slugValid && form.slug && <span className="text-rose-400 ml-2">slug invalide (lettres, chiffres, tirets)</span>}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email société *</Label>
              <Input data-testid="create-company-email" type="email" value={form.email} onChange={setF("email")} placeholder="contact@elite.fr" />
            </div>
            <div className="space-y-1.5">
              <Label>Téléphone *</Label>
              <Input data-testid="create-company-phone" value={form.phone} onChange={setF("phone")} placeholder="+33 1 00 00 00 00" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>WhatsApp</Label>
              <Input data-testid="create-company-whatsapp" value={form.whatsapp_number} onChange={setF("whatsapp_number")} placeholder="+33 6 00 00 00 00" />
            </div>
            <div className="space-y-1.5">
              <Label>Couleur principale</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="create-company-color" type="color" value={form.primary_color} onChange={setF("primary_color")} className="h-10 w-14 p-1" />
                <Input value={form.primary_color} onChange={setF("primary_color")} className="flex-1 font-mono text-sm" />
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-800">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Premier administrateur (optionnel)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nom admin</Label>
                <Input data-testid="create-admin-name" value={form.admin_name} onChange={setF("admin_name")} placeholder="Pierre Dupont" />
              </div>
              <div className="space-y-1.5">
                <Label>Email admin</Label>
                <Input data-testid="create-admin-email" type="email" value={form.admin_email} onChange={setF("admin_email")} placeholder="admin@elite.fr" />
              </div>
            </div>
            <div className="space-y-1.5 mt-3">
              <Label>Mot de passe temporaire</Label>
              <Input data-testid="create-admin-password" type="text" value={form.admin_password} onChange={setF("admin_password")} placeholder="min. 4 caractères" />
              <p className="text-xs text-zinc-500">L'admin pourra le changer à sa première connexion.</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Annuler</Button>
          <Button data-testid="create-company-submit" onClick={submit} disabled={!required || saving}
                  className="bg-amber-500 hover:bg-amber-400 text-black">
            {saving ? "Création…" : "Créer la société"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Edit modal

function EditCompanyDialog({ company, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!company) { setForm(null); return; }
    const b = company.branding || {};
    setForm({
      company_name: company.company_name || "",
      slug:         company.slug || "",
      email:        company.email || "",
      phone:        company.phone || "",
      whatsapp_number: company.whatsapp_number || "",
      primary_color: b.primary_color || "#F59E0B",
      logo_url:      b.logo_url || "",
      headline:      company.headline || "",
      subheadline:   company.subheadline || "",
      services:      (company.services || []).join("\n"),
      // Phase 28.5 — `trust_points` editable. Render one per line. Each
      // line accepts either "Title" or "Title — Description" (em-dash);
      // the landing page renders each as a feature card with that split.
      trust_points:  (company.trust_points || []).map((tp) =>
        typeof tp === "string" ? tp
          : [tp?.title || "", tp?.desc || ""].filter(Boolean).join(" — ")
      ).join("\n"),
      legal_name:    company.legal_name || "",
      address:       company.address || "",
      siret:         company.siret || "",
      vat_status:    company.vat_status || "non_assujetti",
      vat_number:    company.vat_number || "",
      payment_terms: company.payment_terms || "",
      late_penalty_rate: company.late_penalty_rate || "",
    });
  }, [company]);

  const setF = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  // Logo upload — Phase 28.5. We send the raw file to the backend which
  // converts it to a `data:` URL and stores it on the company doc. The
  // returned `branding.logo_url` is then patched into local form state so
  // the preview is instant.
  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo trop lourd (max 2 Mo)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/super/companies/${company.company_id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, logo_url: data?.branding?.logo_url || p.logo_url }));
      toast.success("Logo téléversé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Téléversement échoué");
    } finally {
      // reset the input so re-uploading the same file works
      e.target.value = "";
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        slug:         form.slug.trim(),
        email:        form.email.trim(),
        phone:        form.phone.trim(),
        whatsapp_number: form.whatsapp_number.trim(),
        branding:     { primary_color: form.primary_color, logo_url: form.logo_url.trim() },
        headline:     form.headline,
        subheadline:  form.subheadline,
        services:     form.services.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
        trust_points: form.trust_points.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
        legal_name:   form.legal_name.trim(),
        address:      form.address.trim(),
        siret:        form.siret.trim(),
        vat_status:   form.vat_status,
        vat_number:   form.vat_number.trim(),
        payment_terms: form.payment_terms.trim(),
        late_penalty_rate: form.late_penalty_rate.trim(),
      };
      const { data } = await api.put(`/super/companies/${company.company_id}`, payload);
      toast.success("Société mise à jour");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mise à jour échouée");
    } finally {
      setSaving(false);
    }
  };

  if (!company || !form) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="edit-company-title">Modifier {company.company_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Basic */}
          <section className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nom société</Label>
                <Input data-testid="edit-company-name" value={form.company_name} onChange={setF("company_name")} /></div>
              <div className="space-y-1.5"><Label>Slug</Label>
                <Input data-testid="edit-company-slug" value={form.slug} onChange={setF("slug")} className="font-mono" /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input type="email" value={form.email} onChange={setF("email")} /></div>
              <div className="space-y-1.5"><Label>Téléphone</Label>
                <Input value={form.phone} onChange={setF("phone")} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>WhatsApp</Label>
                <Input data-testid="edit-company-whatsapp" value={form.whatsapp_number} onChange={setF("whatsapp_number")} /></div>
            </div>
          </section>
          {/* Branding */}
          <section className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Identité visuelle</p>
            <div className="space-y-1.5">
              <Label>Couleur principale</Label>
              <div className="flex items-center gap-2">
                <Input type="color" value={form.primary_color} onChange={setF("primary_color")} className="h-10 w-14 p-1" />
                <Input value={form.primary_color} onChange={setF("primary_color")} className="flex-1 font-mono text-sm" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded border border-zinc-700 bg-[#0a0a0a] flex items-center justify-center overflow-hidden shrink-0">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="max-h-12 max-w-12 object-contain" />
                  ) : (
                    <span className="text-zinc-600 text-xs">aucun</span>
                  )}
                </div>
                <div className="flex-1 space-y-2 min-w-0">
                  <label data-testid="edit-company-logo-upload"
                         className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-xs font-medium border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 cursor-pointer w-full sm:w-auto">
                    Téléverser un logo
                    <input type="file" accept="image/*" onChange={onLogoUpload} className="hidden" />
                  </label>
                  <p className="text-[11px] text-zinc-500">PNG, JPG ou SVG. Max 2 Mo.</p>
                </div>
                {form.logo_url && (
                  <Button type="button" variant="ghost" size="sm"
                          onClick={() => setForm((p) => ({ ...p, logo_url: "" }))}
                          className="text-xs text-zinc-400 hover:text-rose-300">
                    Retirer
                  </Button>
                )}
              </div>
              <details className="mt-2 text-xs text-zinc-500">
                <summary className="cursor-pointer hover:text-zinc-300">Ou coller une URL externe</summary>
                <Input data-testid="edit-company-logo" value={form.logo_url} onChange={setF("logo_url")}
                       placeholder="https://…/logo.png" className="mt-2" />
              </details>
            </div>
          </section>
          {/* Content */}
          <section className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Page d'accueil</p>
            <div className="space-y-1.5"><Label>Titre principal</Label>
              <Input data-testid="edit-company-headline" value={form.headline} onChange={setF("headline")} placeholder="Votre transport haut de gamme" /></div>
            <div className="space-y-1.5"><Label>Sous-titre</Label>
              <Textarea data-testid="edit-company-subheadline" value={form.subheadline} onChange={setF("subheadline")}
                        rows={2} placeholder="Service VTC, 24/7, Paris et Île-de-France" /></div>
            <div className="space-y-1.5"><Label>Services (un par ligne)</Label>
              <Textarea data-testid="edit-company-services" value={form.services} onChange={setF("services")} rows={3}
                        placeholder="Transferts aéroport&#10;Événementiel&#10;Mise à disposition" /></div>
            <div className="space-y-1.5">
              <Label>Pourquoi nous choisir (max 3, format <code>Titre — Description</code>)</Label>
              <Textarea data-testid="edit-company-trust" value={form.trust_points} onChange={setF("trust_points")} rows={3}
                        placeholder="Sans compte — Demande en 30 secondes&#10;Tarif transparent — Prix TTC confirmé&#10;Service haut de gamme — Chauffeur professionnel" />
              <p className="text-[11px] text-zinc-500">Une ligne par carte. Le titre est en gras, la description en gris.</p>
            </div>
          </section>
          {/* Legal */}
          <section className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Facturation & mentions légales</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Raison sociale</Label>
                <Input data-testid="edit-legal-name" value={form.legal_name} onChange={setF("legal_name")} /></div>
              <div className="space-y-1.5"><Label>SIRET</Label>
                <Input data-testid="edit-siret" value={form.siret} onChange={setF("siret")} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Adresse</Label>
                <Input data-testid="edit-address" value={form.address} onChange={setF("address")} /></div>
              <div className="space-y-1.5"><Label>Statut TVA</Label>
                <Select value={form.vat_status} onValueChange={setF("vat_status")}>
                  <SelectTrigger data-testid="edit-vat-status" className="bg-zinc-900 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectItem value="non_assujetti">Non assujetti (franchise en base)</SelectItem>
                    <SelectItem value="assujetti">Assujetti</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>N° TVA</Label>
                <Input data-testid="edit-vat-number" value={form.vat_number} onChange={setF("vat_number")} placeholder="FR…" /></div>
              <div className="space-y-1.5"><Label>Conditions de paiement</Label>
                <Input data-testid="edit-payment-terms" value={form.payment_terms} onChange={setF("payment_terms")} placeholder="30 jours fin de mois" /></div>
              <div className="space-y-1.5"><Label>Pénalités de retard</Label>
                <Input data-testid="edit-late-penalty" value={form.late_penalty_rate} onChange={setF("late_penalty_rate")} placeholder="3x taux BCE" /></div>
            </div>
          </section>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Annuler</Button>
          <Button data-testid="edit-company-submit" onClick={submit} disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-black">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Account modal — Phase 28.8.
//
// Self-service edit of the super_admin's own email + password.
// Hits the existing `PUT /api/auth/profile` endpoint (used by every role
// including super_admin) which already enforces:
//   • current_password required for email or password change
//   • unique email constraint
//   • password length ≥ 4
// No backend changes were needed; this is strictly a UI surface.

function AccountDialog({ user, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: user?.email || "",
    current_password: "",
    password: "",
    confirm_password: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      email: user?.email || "",
      current_password: "",
      password: "",
      confirm_password: "",
    });
  }, [open, user]);

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    const wantsEmail = form.email.trim() && form.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
    const wantsPwd   = !!form.password;

    if (!wantsEmail && !wantsPwd) {
      toast.info("Aucun changement à enregistrer");
      return;
    }
    if (!form.current_password) {
      toast.error("Mot de passe actuel requis");
      return;
    }
    if (wantsPwd) {
      if (form.password.length < 8) { toast.error("Nouveau mot de passe : 8 caractères minimum"); return; }
      if (form.password !== form.confirm_password) { toast.error("Les mots de passe ne correspondent pas"); return; }
    }

    const payload = { current_password: form.current_password };
    if (wantsEmail) payload.email = form.email.trim();
    if (wantsPwd)   payload.password = form.password;

    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", payload);
      toast.success("Compte mis à jour");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mise à jour échouée");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="account-title">Mon compte</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input data-testid="account-email" type="email" value={form.email} onChange={setF("email")} autoComplete="email" />
          </div>
          <div className="space-y-1.5">
            <Label>Mot de passe actuel</Label>
            <Input data-testid="account-current-password" type="password" value={form.current_password}
                   onChange={setF("current_password")} autoComplete="current-password"
                   placeholder="Requis pour changer email ou mot de passe" />
          </div>
          <div className="pt-2 border-t border-zinc-800/60">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Nouveau mot de passe (optionnel)</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nouveau mot de passe</Label>
                <Input data-testid="account-new-password" type="password" value={form.password}
                       onChange={setF("password")} autoComplete="new-password" placeholder="8 caractères minimum" />
              </div>
              <div className="space-y-1.5">
                <Label>Confirmer le nouveau mot de passe</Label>
                <Input data-testid="account-confirm-password" type="password" value={form.confirm_password}
                       onChange={setF("confirm_password")} autoComplete="new-password" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Annuler</Button>
          <Button data-testid="account-submit" onClick={submit} disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-black">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Main list

export default function SuperDashboard() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAccount, setShowAccount] = useState(false);

  // Soft-guard (the route is also protected at the router level).
  useEffect(() => {
    if (user && user.role !== "super_admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/companies");
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement échoué");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "suspended" : "active";
    try {
      await api.put(`/super/companies/${c.company_id}`, { status: next });
      toast.success(next === "suspended" ? "Société suspendue" : "Société réactivée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Changement de statut échoué");
    }
  };

  // Copy an onboarding kit for an existing row. Admin credentials are not
  // available post-creation (the password is hashed in DB), so this kit
  // includes the public links + the generic login URL only. The full kit
  // with the temporary password is shown right after tenant creation in
  // the CreateCompanyDialog.
  const copyKitForRow = (c) => {
    const msg = buildOnboardingMessage({
      companyName: c.company_name,
      links: companyLinks(c.slug),
      loginUrl: `${publicOrigin()}/login`,
      adminEmail: null,
      tempPwd: null,
    });
    copyToClipboard(msg);
  };

  const rows = useMemo(() => companies, [companies]);

  return (
    <div data-testid="super-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-zinc-800/60 bg-[#050505]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Buildings size={20} className="text-amber-400" />
          <h1 className="text-base sm:text-lg font-semibold">S.DRIVE · Super Admin</h1>
          <span className="hidden sm:inline text-xs text-zinc-500">· {user?.email}</span>
          <div className="flex-1" />
          <ThemeToggle testid="super-theme" />
          <Button data-testid="super-account" variant="ghost" size="sm"
                  onClick={() => setShowAccount(true)}
                  className="text-zinc-300 hover:text-white">
            <User size={16} className="mr-1.5" /> Mon compte
          </Button>
          <Button data-testid="super-logout" variant="ghost" size="sm" onClick={logout}
                  className="text-zinc-300 hover:text-white">
            <SignOut size={16} className="mr-1.5" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Sociétés</p>
            <p className="text-2xl font-semibold mt-1">{rows.length} tenant{rows.length > 1 ? "s" : ""}</p>
          </div>
          <Button data-testid="super-create-company" onClick={() => setShowCreate(true)}
                  className="bg-amber-500 hover:bg-amber-400 text-black">
            <Plus size={16} className="mr-1.5" /> Nouvelle société
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-zinc-500">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-lg">
            Aucune société. Cliquez sur « Nouvelle société » pour commencer.
          </div>
        ) : (
          <>
            {/* Desktop / tablet — table layout */}
            <div className="hidden md:block overflow-x-auto border border-zinc-800 rounded-lg bg-[#0b0b0b]">
              <table className="w-full text-sm">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Société</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Téléphone</th>
                    <th className="text-left px-4 py-3">Statut</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const links = companyLinks(c.slug);
                    const active = c.status === "active";
                    return (
                      <tr key={c.company_id} data-testid={`company-row-${c.slug}`} className="border-t border-zinc-800/70">
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-start gap-3 min-w-0">
                            <CompanyAvatar c={c} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{c.company_name}</p>
                              <p className="text-xs text-zinc-500 font-mono truncate">/{c.slug}</p>
                              <CopyLinkChips slug={c.slug} links={links} />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-zinc-300">{c.email || "—"}</td>
                        <td className="px-4 py-3 align-top text-zinc-300 hidden lg:table-cell">{c.phone || "—"}</td>
                        <td className="px-4 py-3 align-top">
                          <StatusPill slug={c.slug} active={active} />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <RowActions c={c} active={active} links={links}
                                      onKit={() => copyKitForRow(c)} onEdit={() => setEditing(c)}
                                      onToggle={() => toggleStatus(c)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile — vertical card layout for readability */}
            <div className="md:hidden space-y-3">
              {rows.map((c) => {
                const links = companyLinks(c.slug);
                const active = c.status === "active";
                return (
                  <div key={c.company_id} data-testid={`company-row-${c.slug}`}
                       className="border border-zinc-800 rounded-lg bg-[#0b0b0b] p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <CompanyAvatar c={c} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{c.company_name}</p>
                        <p className="text-xs text-zinc-500 font-mono truncate">/{c.slug}</p>
                      </div>
                      <StatusPill slug={c.slug} active={active} />
                    </div>
                    {(c.email || c.phone) && (
                      <div className="text-xs text-zinc-400 space-y-0.5 border-t border-zinc-800/60 pt-2">
                        {c.email && <p className="break-all">{c.email}</p>}
                        {c.phone && <p>{c.phone}</p>}
                      </div>
                    )}
                    <CopyLinkChips slug={c.slug} links={links} />
                    <div className="border-t border-zinc-800/60 pt-3 flex items-center justify-end gap-1">
                      <RowActions c={c} active={active} links={links}
                                  onKit={() => copyKitForRow(c)} onEdit={() => setEditing(c)}
                                  onToggle={() => toggleStatus(c)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      <CreateCompanyDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { load(); /* modal stays open on the kit view until user closes */ }}
      />
      <EditCompanyDialog
        company={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
      <AccountDialog
        user={user}
        open={showAccount}
        onClose={() => setShowAccount(false)}
        onSaved={(updated) => {
          setShowAccount(false);
          // Sync the cached user so the email shown in the header
          // refreshes immediately. The JWT remains valid (its `sub` is
          // the user id, not the email) so no re-login is required.
          if (updated && typeof setUser === "function") {
            setUser((prev) => ({ ...(prev || {}), ...updated }));
          }
        }}
      />
    </div>
  );
}
